import { ICellRendererParams } from 'ag-grid-community'
import { Box, IconButton } from '@mui/material'
import theme from 'theme'
import { ExclusionRule } from 'models'
import OnyxiaTooltip from 'ui/components/tooltip/OnyxiaTooltip'
import { useThemeColors } from 'hooks/useThemeColors'
import {
  NO_DELETE_PERMISSION_MESSAGE,
  NO_EDIT_PERMISSION_MESSAGE
} from 'ui/components/modal/variants/exclusion-rule/exclusionRule.constants'
import { PencilIcon } from 'ui/components/modal/variants/exclusion-rule/exclusionRuleIcons'

const TrashIcon = () => {
  const getColor = useThemeColors()

  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M13.3333 5V4.33333C13.3333 3.39991 13.3333 2.9332 13.1517 2.57668C12.9919 2.26308 12.7369 2.00811 12.4233 1.84832C12.0668 1.66667 11.6001 1.66667 10.6667 1.66667H9.33333C8.39991 1.66667 7.9332 1.66667 7.57668 1.84832C7.26308 2.00811 7.00811 2.26308 6.84832 2.57668C6.66667 2.9332 6.66667 3.39991 6.66667 4.33333V5M8.33333 9.58333V13.75M11.6667 9.58333V13.75M2.5 5H17.5M15.8333 5V14.3333C15.8333 15.7335 15.8333 16.4335 15.5608 16.9683C15.3212 17.4387 14.9387 17.8212 14.4683 18.0608C13.9335 18.3333 13.2335 18.3333 11.8333 18.3333H8.16667C6.76654 18.3333 6.06647 18.3333 5.53169 18.0608C5.06129 17.8212 4.67883 17.4387 4.43915 16.9683C4.16667 16.4335 4.16667 15.7335 4.16667 14.3333V5"
        stroke={getColor('menu-item-text')}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const tooltipSx = (width: string) => ({
  tooltip: {
    sx: {
      width,
      minHeight: '41px',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: `${theme.colors.menuItemText} !important`,
      fontFamily: `${theme.typography.fontFamily.primary} !important`,
      fontSize: '12px !important',
      fontWeight: '600 !important',
      lineHeight: '140% !important'
    }
  }
})

interface RulesActionsCellRendererProps extends ICellRendererParams<ExclusionRule> {
  onEdit?: (rule: ExclusionRule, e: React.MouseEvent) => void
  onDelete?: (rule: ExclusionRule, e: React.MouseEvent) => void
  canEdit?: boolean
  canDelete?: boolean
}

const RulesActionsCellRenderer = (props: RulesActionsCellRendererProps) => {
  const { data, onEdit, onDelete, canEdit = true, canDelete = true } = props
  if (!data) return null

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '4px',
        height: '100%'
      }}
    >
      <OnyxiaTooltip
        title={canEdit ? 'Edit Exclusion Rule' : NO_EDIT_PERMISSION_MESSAGE}
        placement="bottom"
        componentsProps={tooltipSx(canEdit ? '145px' : '280px')}
      >
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <IconButton
            size="small"
            disabled={!canEdit}
            onClick={(e) => onEdit?.(data, e)}
            aria-label="Edit rule"
            sx={{
              color: theme.colors.menuItemText,
              '&:hover': { color: theme.colors.devicesSecondaryText },
              '&.Mui-disabled': { color: theme.colors.menuItemText, opacity: 0.4 }
            }}
          >
            <PencilIcon />
          </IconButton>
        </Box>
      </OnyxiaTooltip>
      <OnyxiaTooltip
        title={canDelete ? 'Delete Exclusion Rule' : NO_DELETE_PERMISSION_MESSAGE}
        placement="bottom-end"
        componentsProps={tooltipSx(canDelete ? '145px' : '280px')}
      >
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <IconButton
            size="small"
            disabled={!canDelete}
            onClick={(e) => onDelete?.(data, e)}
            aria-label="Delete rule"
            sx={{
              '&:hover svg path': { stroke: theme.colors.devicesSecondaryText },
              '&.Mui-disabled': { opacity: 0.4 }
            }}
          >
            <TrashIcon />
          </IconButton>
        </Box>
      </OnyxiaTooltip>
    </Box>
  )
}

export default RulesActionsCellRenderer
